import { useState, useEffect, useCallback, Suspense } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import {
  Loader2,
  CalendarRange,
  Ship,
  MapPin,
  ArrowRight,
  Clock,
  Check,
  Pencil,
  X,
} from 'lucide-react'
import Sidebar from '../components/Sidebar'
import TopBar from '../components/TopBar'
import CommandPalette from '../components/CommandPalette'
import { VesselProvider } from '../contexts/VesselContext'
import { UnreadCountsProvider } from '../contexts/UnreadCountsContext'
import { itinerary as itineraryApi, type FleetItinerary, type ItineraryLeg } from '../api'

// Alt+1 through Alt+9 navigate to the first 9 sidebar pages
const shortcutRoutes = [
  '/lng-ops',                     // Alt+1 → Dashboard
  '/lng-ops/live-view',            // Alt+2 → Live View
  '/lng-ops/vessels',              // Alt+3 → Vessels
  '/lng-ops/voyage-performance',   // Alt+4 → Voyage Performance
  '/lng-ops/bog-management',       // Alt+5 → BOG Management
  '/lng-ops/vessel-reports',       // Alt+6 → Vessel Reports
  '/lng-ops/graphs',               // Alt+7 → Graphs
  '/lng-ops/reports',              // Alt+8 → Reports
  '/lng-ops/scheduler',            // Alt+9 → Scheduler
]

function PageLoadingFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-5 w-5 animate-spin text-accent" />
    </div>
  )
}

function formatShortDateTime(iso: string | null): string {
  if (!iso) return '--'
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  })
}

function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ── Review Modal ────────────────────────────────────────────────────

function ReviewModal({ onDone }: { onDone: () => void }) {
  const [fleet, setFleet] = useState<FleetItinerary[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [editingLeg, setEditingLeg] = useState<number | null>(null)
  const [editForm, setEditForm] = useState({ to_datetime: '' })
  const [savingEdit, setSavingEdit] = useState(false)

  useEffect(() => {
    itineraryApi.fleet().then((data) => {
      setFleet(data)
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const handleConfirm = async () => {
    setSubmitting(true)
    try {
      await itineraryApi.submitReview()
      onDone()
    } catch {
      // still allow closing
      onDone()
    } finally {
      setSubmitting(false)
    }
  }

  const handleStartEdit = (leg: ItineraryLeg) => {
    setEditingLeg(leg.id)
    setEditForm({ to_datetime: toLocalInput(leg.to_datetime) })
  }

  const handleSaveEdit = async (leg: ItineraryLeg) => {
    if (!editForm.to_datetime) return
    setSavingEdit(true)
    try {
      const updated = await itineraryApi.update(leg.id, {
        to_datetime: new Date(editForm.to_datetime).toISOString(),
      })
      // Update in local state
      setFleet((prev) =>
        prev.map((f) => ({
          ...f,
          current_leg: f.current_leg?.id === leg.id ? { ...f.current_leg, ...updated } : f.current_leg,
          next_leg: f.next_leg?.id === leg.id ? { ...f.next_leg, ...updated } : f.next_leg,
          legs: f.legs.map((l) => (l.id === leg.id ? { ...l, ...updated } : l)),
        }))
      )
      setEditingLeg(null)
    } catch {
      // ignore
    } finally {
      setSavingEdit(false)
    }
  }

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })

  // Vessels that have legs (either current or next)
  const vesselsWithActivity = fleet.filter(
    (f) => f.current_leg || f.next_leg || f.legs.length > 0
  )
  const vesselsIdle = fleet.filter(
    (f) => !f.current_leg && !f.next_leg && f.legs.length === 0
  )

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-[800px] max-h-[90vh] flex flex-col rounded-xl bg-bg-secondary shadow-2xl overflow-hidden mx-4">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10">
              <CalendarRange className="h-5 w-5 text-accent" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-text-primary">Daily Itinerary Review</h2>
              <p className="text-2xs text-text-muted">{today}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-accent" />
            </div>
          ) : fleet.length === 0 ? (
            <div className="text-center py-12 text-text-muted">
              <Ship className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No vessels assigned</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Vessels with activity */}
              {vesselsWithActivity.map((v) => (
                <div key={v.carrier_id} className="rounded-lg bg-bg-tertiary p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Ship className="h-3.5 w-3.5 text-accent" />
                    <span className="text-xs font-semibold text-text-primary">{v.carrier_name}</span>
                  </div>

                  {/* Current leg */}
                  {v.current_leg && (
                    <ReviewLegRow
                      label="En Route"
                      labelColor="text-accent"
                      leg={v.current_leg}
                      editingLeg={editingLeg}
                      editForm={editForm}
                      setEditForm={setEditForm}
                      savingEdit={savingEdit}
                      onStartEdit={handleStartEdit}
                      onSaveEdit={handleSaveEdit}
                      onCancelEdit={() => setEditingLeg(null)}
                    />
                  )}

                  {/* Next leg */}
                  {v.next_leg && (
                    <ReviewLegRow
                      label="Next"
                      labelColor="text-text-muted"
                      leg={v.next_leg}
                      editingLeg={editingLeg}
                      editForm={editForm}
                      setEditForm={setEditForm}
                      savingEdit={savingEdit}
                      onStartEdit={handleStartEdit}
                      onSaveEdit={handleSaveEdit}
                      onCancelEdit={() => setEditingLeg(null)}
                    />
                  )}

                  {!v.current_leg && !v.next_leg && v.legs.length > 0 && (
                    <p className="text-2xs text-text-muted">{v.legs.length} leg(s) — all completed</p>
                  )}
                </div>
              ))}

              {/* Idle vessels (collapsed) */}
              {vesselsIdle.length > 0 && (
                <div className="rounded-lg bg-bg-tertiary/50 p-3">
                  <p className="text-2xs text-text-muted">
                    {vesselsIdle.length} vessel{vesselsIdle.length > 1 ? 's' : ''} with no itinerary:{' '}
                    <span className="text-text-secondary">
                      {vesselsIdle.map((v) => v.carrier_name).join(', ')}
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-3 flex items-center justify-end gap-3">
          <p className="flex-1 text-2xs text-text-muted">
            Review your fleet's itinerary and update ETAs if needed.
          </p>
          <button
            onClick={handleConfirm}
            disabled={submitting || loading}
            className="flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-xs font-medium text-accent-fg transition-colors hover:bg-accent/90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Confirm Review
          </button>
        </div>
      </div>
    </div>
  )
}

function ReviewLegRow({
  label,
  labelColor,
  leg,
  editingLeg,
  editForm,
  setEditForm,
  savingEdit,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
}: {
  label: string
  labelColor: string
  leg: ItineraryLeg
  editingLeg: number | null
  editForm: { to_datetime: string }
  setEditForm: (f: { to_datetime: string }) => void
  savingEdit: boolean
  onStartEdit: (leg: ItineraryLeg) => void
  onSaveEdit: (leg: ItineraryLeg) => void
  onCancelEdit: () => void
}) {
  const isEditing = editingLeg === leg.id

  return (
    <div className="flex items-center gap-2 py-1.5">
      <span className={`text-2xs font-medium w-14 ${labelColor}`}>{label}</span>
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        <MapPin className="h-3 w-3 text-text-muted flex-shrink-0" />
        <span className="text-xs text-text-primary truncate">{leg.from_port}</span>
        <ArrowRight className="h-3 w-3 text-text-muted flex-shrink-0" />
        <MapPin className="h-3 w-3 text-text-muted flex-shrink-0" />
        <span className="text-xs text-text-primary truncate">{leg.to_port}</span>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {isEditing ? (
          <>
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-accent" />
              <span className="text-2xs text-text-muted">ETA:</span>
              <input
                type="datetime-local"
                value={editForm.to_datetime}
                onChange={(e) => setEditForm({ to_datetime: e.target.value })}
                className="rounded border border-accent/30 bg-bg-tertiary px-1.5 py-0.5 text-2xs text-text-primary focus:border-accent focus:outline-none"
              />
            </div>
            <button
              onClick={() => onSaveEdit(leg)}
              disabled={savingEdit}
              className="flex h-6 w-6 items-center justify-center rounded text-positive hover:bg-positive/10 transition-colors disabled:opacity-40"
            >
              {savingEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            </button>
            <button
              onClick={onCancelEdit}
              className="flex h-6 w-6 items-center justify-center rounded text-text-muted hover:bg-bg-hover transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </>
        ) : (
          <>
            <Clock className="h-3 w-3 text-text-muted" />
            <span className="text-2xs text-text-muted font-mono">{formatShortDateTime(leg.to_datetime)}</span>
            <button
              onClick={() => onStartEdit(leg)}
              className="flex h-6 w-6 items-center justify-center rounded text-text-muted hover:bg-bg-hover hover:text-accent transition-colors"
              title="Edit ETA"
            >
              <Pencil className="h-3 w-3" />
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ── Main Layout ─────────────────────────────────────────────────────

export default function DashboardLayout() {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const navigate = useNavigate()

  const openPalette = useCallback(() => setCommandPaletteOpen(true), [])
  const closePalette = useCallback(() => setCommandPaletteOpen(false), [])

  // Check review status on mount — non-blocking (layout renders immediately)
  useEffect(() => {
    itineraryApi.reviewStatus().then((status) => {
      if (!status.reviewed) {
        setShowReviewModal(true)
      }
    }).catch(() => {
      // If API fails (e.g. no itinerary tables yet), skip the modal
    })
  }, [])

  // Global Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setCommandPaletteOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  // Alt+1 through Alt+9 keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Skip when focus is in an input, textarea, or select
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        const num = parseInt(e.key, 10)
        if (num >= 1 && num <= 9) {
          e.preventDefault()
          navigate(shortcutRoutes[num - 1])
        }
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [navigate])

  return (
    <UnreadCountsProvider>
      <div className="flex h-screen overflow-hidden bg-bg-primary">
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-hidden">
          <TopBar onOpenCommandPalette={openPalette} />
          <main className="flex-1 overflow-y-auto p-4">
            <VesselProvider>
              <Suspense fallback={<PageLoadingFallback />}>
                <Outlet />
              </Suspense>
            </VesselProvider>
          </main>
        </div>
        <CommandPalette open={commandPaletteOpen} onClose={closePalette} />

        {/* Daily itinerary review modal — renders after layout is visible (non-blocking) */}
        {showReviewModal && (
          <ReviewModal onDone={() => setShowReviewModal(false)} />
        )}
      </div>
    </UnreadCountsProvider>
  )
}
